// src/lib/caution-window.js — fenêtre J-2 d'ouverture de la caution (CommonJS).
//
// ⚠️ SOURCE UNIQUE pour le serveur ET l'écran (F10 phase 2, 03/10). Écrit en
// CommonJS pur, zéro dépendance : le contrat côté écran
// (src/lib/contrat-vars.js) lit la MÊME fenêtre ; le serveur le lit par le
// relais `lib/caution-window.js`. Ne rien ajouter qui ne soit chargeable des
// deux côtés (garde : src/lib/doublons-lib-conformite.test.js).
//
// EXTRACTION (P1a) : ces helpers vivaient inline dans api/sites.js (handlers
// journey_read / checkout_start). Ils sont remontés ici pour être PARTAGÉS avec
// api/stripe.js (handler create_payment_session) — un endpoint api/ ne doit
// JAMAIS en require() un autre (cycle + couplage). api/sites.js conserve des
// re-exports thin (rétro-compat tests + signature historique inchangée).
//
// Comportement IDENTIQUE à l'ancien _computeCautionAvailable / _todayParisISO :
// la caution n'est ouverte que ≤ JOURNEY_CAUTION_WINDOW_DAYS jours avant
// l'arrivée (aligné hold 7j ADR-174 + UX). Comparaison en date civile Paris.

// Fenêtre d'ouverture caution avant arrivée (jours). Aligné ADR-174 (hold 7j).
const JOURNEY_CAUTION_WINDOW_DAYS = 2;

// ── BORNE DE FIN (chantier « une date de fin sur la caution ») ───────────────
//
// 🔴 LE TROU QUE CETTE CONSTANTE FERME : jusqu'ici la fenêtre n'avait AUCUNE
// borne de fin, et c'était assumé (« Robuste aux start_date passés (séjour en
// cours → diff négatif ≤ 2 → true) »). La seule fermeture était structurelle —
// la neutralisation post_stay — et elle est `confirmed`-only côté Direct. Donc
// une réservation `paid_stay` JAMAIS confirmée gardait une caution payable
// INDÉFINIMENT après le départ : un lien ressorti d'un vieil e-mail encaissait
// une caution six mois après, sur un séjour terminé, sans rien garantir.
//
// DÉLAI RETENU : fin de séjour + 2 jours (décision Dimitri, ce lot).
//   - la caution garantit LE SÉJOUR : passé le départ elle n'a plus d'objet,
//     et un dépôt encaissé « pour rien » est pire qu'un lien mort ;
//   - 2 jours = SYMÉTRIE avec la fenêtre d'ouverture (J-2) : une seule phrase
//     lisible pour le voyageur (« de 2 jours avant l'arrivée à 2 jours après
//     le départ »), aucun nombre magique de plus ;
//   - la marge absorbe le paiement fait le soir du départ après minuit et le
//     décalage de fuseau d'un voyageur étranger.
// Réglage INDÉPENDANT de l'ouverture (même valeur aujourd'hui, pas la même
// constante) : déplacer l'un ne doit pas déplacer l'autre par accident.
const CAUTION_WINDOW_GRACE_DAYS_AFTER_END = 2;

/**
 * Renvoie la date du jour au format YYYY-MM-DD dans le fuseau Europe/Paris.
 *
 * 🔴 Pourquoi pas `new Date().toISOString().slice(0,10)` : ça donne la date
 * UTC. Entre 22h et minuit (heure de Paris l'été, UTC+2), UTC est encore la
 * veille → un séjour qui commence « demain » serait vu à J-2 au lieu de J-1.
 * On dérive la date civile Paris via Intl (en-CA → YYYY-MM-DD).
 *
 * @param {Date} [now] — injectable pour tests déterministes.
 * @returns {string} 'YYYY-MM-DD'
 */
function todayParisISO(now = new Date()) {
  // en-CA formate en YYYY-MM-DD ; timeZone applique le décalage civil Paris.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Paris',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
}

/**
 * caution_available : true si (start_date − aujourd'hui_Paris) ≤ fenêtre jours.
 *
 * Comparaison en jours civils purs (start_date est un `date` Postgres sans
 * heure). On parse les deux ISO à minuit UTC : la soustraction donne un nombre
 * de jours entier exact (pas de DST à minuit UTC). Robuste aux start_date
 * passés (séjour en cours → diff négatif ≤ 2 → true).
 *
 * @param {string} startDateISO — 'YYYY-MM-DD' (date arrivée).
 * @param {string} todayISO — 'YYYY-MM-DD' (aujourd'hui Paris).
 * @returns {boolean}
 */
function computeCautionAvailable(startDateISO, todayISO) {
  if (typeof startDateISO !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(startDateISO)) {
    return false;
  }
  const start = Date.parse(`${startDateISO}T00:00:00Z`);
  const today = Date.parse(`${todayISO}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(today)) return false;
  const diffDays = Math.round((start - today) / 86400000);
  return diffDays <= JOURNEY_CAUTION_WINDOW_DAYS;
}

/**
 * Date d'ouverture de la caution = start_date − JOURNEY_CAUTION_WINDOW_DAYS
 * jours, au format 'YYYY-MM-DD'. Exposée au voyageur (« possible à partir du… »)
 * et dans la réponse 409 `available_from` du garde serveur.
 *
 * @param {string} startDateISO — 'YYYY-MM-DD' (date arrivée).
 * @returns {string|null} 'YYYY-MM-DD' ou null si start_date invalide.
 */
function cautionAvailableFrom(startDateISO) {
  if (typeof startDateISO !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(startDateISO)) {
    return null;
  }
  const start = Date.parse(`${startDateISO}T00:00:00Z`);
  if (!Number.isFinite(start)) return null;
  const from = new Date(start - JOURNEY_CAUTION_WINDOW_DAYS * 86400000);
  return from.toISOString().slice(0, 10);
}

/**
 * computeCautionWindowClosed : la fenêtre de caution est-elle DÉFINITIVEMENT
 * fermée ? true si aujourd'hui (Paris) > end_date + CAUTION_WINDOW_GRACE_DAYS_
 * AFTER_END. Complément de `computeCautionAvailable` (borne de début).
 *
 * 🔴 FAIL-OPEN ASSUMÉ, et c'est l'inverse de la borne de début — pourquoi :
 * une end_date absente ou illisible ne doit JAMAIS fermer une caution (chemin
 * argent : une nouvelle garde ne refuse pas sur une donnée qu'elle ne sait pas
 * lire, cf. doctrine lib/caution-matrix.js). La borne de DÉBUT reste
 * fail-closed sur start_date invalide : les deux ensemble ⇒ une date pourrie
 * refuse déjà tout, sans que cette borne-ci n'invente une fermeture.
 *
 * @param {string} endDateISO — 'YYYY-MM-DD' (date de départ).
 * @param {string} todayISO — 'YYYY-MM-DD' (aujourd'hui Paris).
 * @returns {boolean} true = période de dépôt passée.
 */
function computeCautionWindowClosed(endDateISO, todayISO) {
  if (typeof endDateISO !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(endDateISO)) {
    return false; // fail-open (cf. note ci-dessus)
  }
  if (typeof todayISO !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(todayISO)) {
    return false;
  }
  const end = Date.parse(`${endDateISO}T00:00:00Z`);
  const today = Date.parse(`${todayISO}T00:00:00Z`);
  if (!Number.isFinite(end) || !Number.isFinite(today)) return false;
  // Jours civils purs (minuit UTC des deux côtés → pas de DST).
  const diffDays = Math.round((today - end) / 86400000);
  return diffDays > CAUTION_WINDOW_GRACE_DAYS_AFTER_END;
}

/**
 * Dernier jour où la caution reste déposable = end_date +
 * CAUTION_WINDOW_GRACE_DAYS_AFTER_END, au format 'YYYY-MM-DD'. Exposée au
 * voyageur et dans la réponse 409 `available_until`.
 *
 * @param {string} endDateISO — 'YYYY-MM-DD' (date de départ).
 * @returns {string|null} 'YYYY-MM-DD' ou null si end_date invalide.
 */
function cautionAvailableUntil(endDateISO) {
  if (typeof endDateISO !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(endDateISO)) {
    return null;
  }
  const end = Date.parse(`${endDateISO}T00:00:00Z`);
  if (!Number.isFinite(end)) return null;
  const until = new Date(end + CAUTION_WINDOW_GRACE_DAYS_AFTER_END * 86400000);
  return until.toISOString().slice(0, 10);
}

module.exports = {
  JOURNEY_CAUTION_WINDOW_DAYS,
  CAUTION_WINDOW_GRACE_DAYS_AFTER_END,
  todayParisISO,
  computeCautionAvailable,
  cautionAvailableFrom,
  computeCautionWindowClosed,
  cautionAvailableUntil,
};
